import { css } from "@emotion/css";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Slider,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState, useRef, act } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";
import CustomTextField from "../../components/CustomTextField";
import CustomizedDatePicker from "../../components/CustomizedDatePicker";
import LanguageSelector from "../../components/LanguageSelector";
import {
  generateDaySummarize,
  getSummaryAttemptCount,
} from "../../services/AiService";
import dayjs from "dayjs";
import CommonUtil from "../../util/CommonUtils";
import { AiContext, sidebarContext } from "../../util/context";
import { toast } from "sonner";
import AppContext from "../../config/AppContext";
import { ReactComponent as AiErrorIcon } from "../../assets/images/AI-Error-icon.svg";
import { ReactComponent as AiErrorIconDark } from "../../assets/images/AI-Error-Darkicon.svg";
import { ReactComponent as AIerrorIconRed } from "../../assets/images/Ai-red-error-Icon.svg";
import "./GenerateSummary.scss";
import { getSubscriptionLimit } from "../../services/Subscription";
import UpgradePopup from "../../components/subscription-component/UpgradePopup";
import { useNavigate } from "react-router-dom";
import RestrictPermissionPopup from "../../components/subscription-component/RestrictPermissionPopup";
import TimesheetWeekPicker from "../dashboard/TimesheetWeekPicker";

export default function GenerateSummary({ logdate }) {
  const { t, i18n } = useTranslation();
  const { setGenerateSummary } = useContext(AiContext);
  const { enableAi } = useContext(sidebarContext);
  const { mode } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const { subscriptionDetails } = useContext(sidebarContext);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [restrictPopupOpen, setRestrictPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const timeRef = useRef();
  const [activeTab, setActiveTab] = useState("today");
  const navigate = useNavigate();
  const [state, setState] = useState({
    language: i18n.language || "en",
    wordCount: 200,
    focusAreas: [],
    timeGranularity: "",
    style: t("RAL0678"),
    text: "",
    date: null,
  });

  const [todayCount,setTodaycount] = useState(200);
  const [weekCount,setWeekcount] = useState(200);
  const [customCount,setCustomcount] = useState(200);
  const [errors, setErrors] = useState({ date: "" });
  const [aiLimit, setAiLimit] = useState(0); 
  const [currentAiLimit, setCurrentAiLimit] = useState(0);
  const [isLimitLoading, setIsLimitLoading] = useState(true); // Add loading state for subscription limits
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");

  const [dateErrors, setDateErrors] = useState({
    startDate: "",
    endDate: ""
  });
  const handleCustomStartDateChange = (date) => {
    setCustomStartDate(date);
    setDateErrors(prev => ({ ...prev, startDate: "" }));
  
    if (date && customEndDate) {
      const daysDifference = dayjs(customEndDate).diff(dayjs(date), "day");
  
      if (daysDifference < 0) {
        setDateErrors({
          startDate: t("RAL1003"),
        endDate: t("RAL1003")
      });
    } else if (daysDifference > 7) {
      const newEnd = dayjs(date).add(7, "day").toDate();
      setCustomEndDate(newEnd);
      setDateErrors({
        startDate: t("RAL1004"),
        endDate: t("RAL1004")
      });
    }
    }
  };
  
const handleCustomEndDateChange = (date) => {
  if (!customStartDate) return;
  
  setCustomEndDate(date);
  setDateErrors(prev => ({ ...prev, endDate: "" }));

    const daysDifference = dayjs(date).diff(dayjs(customStartDate), "day");
  
    if (daysDifference < 0) {
      setDateErrors({
      startDate: t("RAL1003"),
      endDate: t("RAL1003")
    });
  } else if (daysDifference > 7) {
    setDateErrors({
      startDate: t("RAL1004"),
      endDate: t("RAL1004")
    });
  }
  };
  

  useEffect(() => {
    if (logdate) {
      const parsedDate = new Date(logdate); 
      setState({
        ...state,
        date: parsedDate
      });
    }
  }, []);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      style: t("RAL0678") 
    }));
  }, [i18n.language]);

  const isActive = ["live", "non_renewing"].includes(subscriptionDetails.status);
  const featureCode = "DAILY_AI_SUMMARIZATION_LIMITS";

  useEffect(() => {
    const fetchFeatureLimitDetails = async () => {
      try {
        const res = await getSubscriptionLimit(featureCode);
        if (res?.data?.code === "RCI0000") {
          setAiLimit(res?.data?.data?.limit);
          setCurrentAiLimit(res?.data?.data?.currValue);
        } else if (res?.data?.code === "RCE0225") {
          if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
            setRestrictPopupOpen(true);
          } else {
            setUpgradePopupOpen(true);
         }
        }
      } catch (error) {
        console.error("Error fetching user details:", error.message);
      } finally {
        setIsLimitLoading(false);
      }
    };
    fetchFeatureLimitDetails();
  }, []);

  const handleSubmit = () => {

    if (hasReachedLimit) {
      if (!["OWNER"].includes(CommonUtil.getRoleCode())) {
        setRestrictPopupOpen(true);
        return;
      } else {
        setUpgradePopupOpen(true);
        return;
      }
    }

    let calculatedStartDate, calculatedEndDate;

    if (activeTab === "today") {
    if (!state.date) {
        setErrors({ date: t("RAL0637") });
        return;
      }
      const tzDate = dayjs.tz(state.date, CommonUtil.getTimeZoneId());
      calculatedStartDate = tzDate.startOf("day");
      calculatedEndDate = tzDate.endOf("day");

      setState({ ...state, wordCount: todayCount });
    }
    else if (activeTab === "custom") {
      const newErrors = {
        startDate: !customStartDate ? t("RAL1007") : "",
        endDate: customStartDate && !customEndDate ? t("RAL1005") : ""
      };
  
      if (newErrors.startDate || (customStartDate && newErrors.endDate)) {
        setDateErrors(newErrors);
        return;
      }
      const daysDifference = dayjs(customEndDate).diff(dayjs(customStartDate), "day");
      if (daysDifference > 7) {
        setDateErrors({
          startDate: t("RAL1004"),
          endDate: t("RAL1004")
        });
        return;
      }
      calculatedStartDate = dayjs.tz(customStartDate, CommonUtil.getTimeZoneId()).startOf("day");
      calculatedEndDate = dayjs.tz(customEndDate, CommonUtil.getTimeZoneId()).endOf("day");

      setState({ ...state, wordCount: customCount });
    }
    else if (activeTab === "thisWeek") {
      if (!startDate || !endDate) {
        toast.error(t("Select a week first"));
        return;
      }
      calculatedStartDate = dayjs.tz(startDate, CommonUtil.getTimeZoneId()).startOf("day");
      calculatedEndDate = dayjs.tz(endDate, CommonUtil.getTimeZoneId()).endOf("day");
      setState({ ...state, wordCount: weekCount });
    }

    if (!calculatedStartDate?.isValid() || !calculatedEndDate?.isValid()) {
      toast.error(t("RAL1006"));
      return;
    }

    const payload = {
      ...state,
      startTime: calculatedStartDate.utc().format(),
      endTime: calculatedEndDate.utc().format(),
    };

    setIsLoading(true);
    setGenerateSummary(undefined);
    generateDaySummarize(payload)
      .then((res) => {
        if (res?.data?.code === "RCI0000" && res?.data?.data?.summary?.summary) {
          setCurrentAiLimit(res?.data?.data?.currValue || currentAiLimit + 1);
          setGenerateSummary(processTextSummary(res.data?.data?.summary?.summary));
        } else if (res.data?.code === "RCE0102") {
          toast.error(t("RAL0638"));
        } else {
          toast.error(t("RAL0508"));
        }
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
      });
  };
useEffect(() => {
  if (activeTab === "thisWeek" && !startDate && !endDate) {
    const initialStart = dayjs().startOf('week');
    const initialEnd = dayjs().endOf('week');
    setStartDate(initialStart.toDate());
    setEndDate(initialEnd.toDate());
  }
}, [activeTab]);

  function processTextSummary(text) {
    const summary = text?.replace(/\"/g, "").trim(); // Clean up the summary text
    const startIndex = summary.indexOf("Projects:");
    const cleanSummary = summary.substring(startIndex).trim();
    const lines = cleanSummary.split("\n"); // Split the summary into lines

    let htmlSummary = '<div class="summary">';
    lines.forEach((line) => {
      if (line.includes(":")) {
        const [label, content] = line.split(":", 2);
        htmlSummary += `<p><strong>${label.trim()}:</strong> ${content.trim()}</p>`;
      } else {
        htmlSummary += `<p>${line.trim()}</p>`;
      }
    });
    htmlSummary += "</div>";
    return htmlSummary;
  }

  const handleDateChange = (date) => {
    if (date === null || isNaN(date.valueOf())) {
      setState((prevState) => ({ ...prevState, date: null }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        date: t("RAL0637"),
      }));
    } else {
      setState((prevState) => ({ ...prevState, date }));
      setErrors((prevErrors) => ({ ...prevErrors, date: "" }));
    }
  };

  const remainingAttempts = aiLimit - currentAiLimit;
  const hasReachedLimit = remainingAttempts <= 0;
  
  const getPopupTitle = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0902") : t("RAL0867");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0850");
    } else {
      return t("RAL0852");
    }
  };
  
  const getPopupContent = (subscriptionDetails, t, isRestrictPopup = false) => {
    if (CommonUtil.getStatusCondition(subscriptionDetails)) {
      return isRestrictPopup ? t("RAL0903") : t("RAL0868");
    } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
      return t("RAL0849");
    } else {
      return t("RAL0851");
    }
  };

  const valueWorldCount = () =>{
    if(activeTab == "custom"){
      return customCount
    } else if (activeTab == "thisWeek") {
      return weekCount;
    } else if (activeTab == "today") {
      return todayCount;
    }
  }

  const handleSliderUpdate = (e) =>{
    if(activeTab == "custom"){
      setCustomcount(e);
    } else if (activeTab == "thisWeek") {
      setWeekcount(e);
    } else if (activeTab == "today") {
      setTodaycount(e);
    }
  }

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-around;
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
          row-gap: 20px;
          margin-bottom: 20px;
          margin-top: 10px;
        `}
      >
        <Typography
          variant="header3"
          sx={{
            color: "var( --text-secondary-80)",
            font: "normal normal bold 18px/20px Gotham",
            letterSpacing: "0px",
          }}
        >
          {t("RAL0346")}
        </Typography>
        <Typography
          sx={{
            color: "var( --text-secondary-80)",
            font: "normal normal normal 16px/23px Gotham",
            letterSpacing: "0px",
          }}
        >
          {t("RAL0347")}
        </Typography>
      </div>
      {!isLimitLoading && (
      <div
        className="ErrorAlignment"
        style={hasReachedLimit ? { background: "rgba(254, 80, 80, 0.1)" , padding:"10px",borderRadius:"10px"} : {}}
        >
         <div className={mode === "dark" ? "iconSummaryDark" : "iconSummaryLight"}>
         {hasReachedLimit ? (
           <AIerrorIconRed style={{ marginBottom: "1rem" }} />
         ) : mode === "dark" ? (
          <AiErrorIconDark style={{ marginTop: "5px" }}/>
         ) : (
          <AiErrorIcon style={{ marginTop: "5px" }}/>
         )}
         </div>
         <span
           className={mode === "dark" ? "summaryError" : "summaryLightError"}
           style={{
            color: hasReachedLimit
              ? (mode === "dark" ? "#f8f8f8" : "#242424") 
              : (mode === "dark" ? "#f8f8f8" : "#242424") 
          }} 
           >
            {hasReachedLimit
                ? t("RAL0764") 
                : t("RAL0763")?.replace("{count}", `${remainingAttempts}/${aiLimit}`)}
          </span>
        </div>
      )}
      <div
        className={css`
          display: flex;
          flex-direction: column;
          row-gap: 20px;
          margin-bottom: 20px;
          height: 70%;
          overflow-y: auto;
        `}
      >
        <LanguageSelector
          disabled={isLoading}
          onChange={(lang) => setState({ ...state, language: lang?.label })}
          disableAppChange={true}
        />
        <div>
          <label>{t("RAL1000")}</label>

          <div>
            <div className={`activetab-panel ${mode === "dark" ? "dark" : ""}`}>
              <button
                className={`activepanel-today 
                  ${activeTab === "today" ? "active" : ""} 
                  ${mode === "dark" ? "dark" : ""}`}
                onClick={() => setActiveTab("today")}
              >
                {t("RAL0050")}
              </button>
              <button
                className={`activepanel-today 
                    ${activeTab === "thisWeek" ? "active" : ""} 
                    ${mode === "dark" ? "dark" : ""}`}
                onClick={() => setActiveTab("thisWeek")}
              >
               {t("RAL0562")}
              </button>
              <button
                className={`activepanel-today 
                    ${activeTab === "custom" ? "active" : ""} 
                    ${mode === "dark" ? "dark" : ""}`}
                onClick={() => setActiveTab("custom")}
              >
                 {t("RAL0837")}
              </button>
            </div>

            {activeTab === "custom" && (
              <div className="customdatepicker-summary">
                <div>
                  <CustomizedDatePicker
                    id="start-date"
                    onChange={handleCustomStartDateChange}
                    label={t("RAL1001")}
                    placeholder={t("RAL1001")}
                    value={customStartDate}
                    maxDate={customEndDate ? customEndDate : undefined}
                    minDate={customEndDate ? dayjs(customEndDate).subtract(7, 'day').toDate() : undefined}
                    error={!!dateErrors.startDate}
                    helperText={dateErrors.startDate}
                  />
                </div>
                <span className="alihmnitemscwnter">-</span>
                <div>
                  <CustomizedDatePicker
                    id="end-date"
                    onChange={handleCustomEndDateChange}
                    label={t("RAL1002")}
                    placeholder={t("RAL1002")}
                    value={customEndDate}
                    minDate={customStartDate}
                    maxDate={
                      customStartDate
                        ? dayjs(customStartDate).add(7, "day").toDate()
                        : null
                    }
                    disabled={!customStartDate}
                    error={!!dateErrors.endDate}
                    helperText={dateErrors.endDate}
                  />
                </div>
              </div>
            )}

            {activeTab === "thisWeek" && (
              <div className="timesheetdatepicker-summary">
                <TimesheetWeekPicker
                  ref={timeRef}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={(date) => {
                    setStartDate(date);
                    // Reset any previous errors
                    setErrors(prev => ({ ...prev, date: "" }));
                  }}
                  setEndDate={(date) => {
                    setEndDate(date);
                    setErrors(prev => ({ ...prev, date: "" }));
                  }}
                  selectedDay={dayjs()}
                />
              </div>
            )}
            {activeTab === "today" && (
              <div className="customdatepicker-summarytoday" >
                <CustomizedDatePicker
                  id="btn-date-aisummary"
                  name="btn-date-aisummary"
                  onChange={handleDateChange}
                  value={state.date}
                  error={Boolean(errors.date.length)}
                  helperText={errors.date}
                  disabled={isLoading}
                  fullWidth
                />
              </div>
            )}
          </div>
        </div>
        {/* <div className="autocomplete-nopadding">
          <Autocomplete
            clearIcon={false}
            value={state.timeGranularity}
            onChange={(event, newValue) => {
              setState({ ...state, timeGranularity: newValue });
            }}
            options={["Full Day", "Morning", "Afternoon", "Evening"]}
            autoHighlight
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  font: `normal normal normal var(--font-size-14) / 26px Gotham`,
                }}
              >
                {option}
              </li>
            )}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t("RAL0349")}
                variant="outlined"
              />
            )}
            disabled={isLoading}
          />
        </div>
        <div className="autocomplete-nopadding">
          <Autocomplete
            multiple={true}
            clearIcon={false}
            value={state.focusAreas}
            onChange={(event, newValue) => {
              setState({ ...state, focusAreas: newValue });
            }}
            options={["Meetings", "Projects"]}
            autoHighlight
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  font: `normal normal normal var(--font-size-14) / 26px Gotham`,
                }}
              >
                {option}
              </li>
            )}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t("RAL0350")}
                variant="outlined"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option}
                  label={option}
                  {...getTagProps({ index })}
                  sx={{
                    "& .MuiChip-label": {
                      font: `normal normal normal var(--font-size-14) / 26px Gotham`,
                    },
                  }}
                />
              ))
            }
            disabled={isLoading}
          />
        </div> */}

        <div className="autocomplete-nopadding">
          <Autocomplete
            clearIcon={false}
            value={state.style}
            onChange={(event, newValue) => {
              setState({ ...state, style: newValue });
            }}
            options={[t("RAL0678"), t("RAL0679"), t("RAL0680")]}
            autoHighlight
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  font: `normal normal normal var(--font-size-14) / 26px Gotham`,
                }}
              >
                {option}
              </li>
            )}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t("RAL0351")}
                variant="outlined"
              />
            )}
            componentsProps={{
              popper: {
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "viewport",
                    },
                  },
                ],
              },
              paper: {
                sx: {
                  "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
                    "&:hover": {
                      backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                    },
                    "&.Mui-selected": {
                      backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                      color: "#FA9C2B",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
                      color: "#FA9C2B",
                    },
                  },
                },
              },
            }}
            disabled={isLoading}
          />
        </div>
        <div
          className={css`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
          `}
        >
          <Typography
            sx={{
              color: "var( --text-secondary-80)",
              font: "normal normal normal 14px/17px Gotham;",
              letterSpacing: "0px",
              opacity: "0.8",
            }}
          >
            {t("RAL0352")}
          </Typography>
          <Slider
            sx={{
              ".MuiSlider-valueLabelOpen": {
                backgroundColor: "#fa9c2b !important",
                font: "normal normal normal 14px/17px Gotham;",
              },
              width: "85%", // Set width to 100% of its container
              margin: "0 auto", // Center it horizontal
            }}
            size="medium"
            defaultValue={state.wordCount}
            value={valueWorldCount()}
            aria-label="Small"
            valueLabelDisplay="auto"
            min={activeTab === "today" ? 150 : 300} 
            max={activeTab === "today" ? 300 : 600}
            color="secondary"
            onChange={(e, v) => handleSliderUpdate(v)}
            disabled={isLoading}
          />
        </div>
      </div>
      {isLoading && (
        <Box
          sx={{
            display: isLoading ? "flex" : "none",
          }}
        >
          <CircularProgress
            style={{
              top: "50%",
              left: "85%",
              position: "fixed",
              zIndex: 999,
            }}
          />
        </Box>
      )}
      <CustomButton
        variant="contained"
        onClick={() => handleSubmit()}
        disabled={isLoading || !enableAi}
        height="auto"
      >
        {t("RAL0353")}
      </CustomButton>

      {["OWNER"].includes(CommonUtil.getRoleCode()) && (
          <UpgradePopup
            open={upgradePopupOpen}
            onClose={() => setUpgradePopupOpen(false)}
            onConfirm={() => {
              if (CommonUtil.getRoleCode() === "OWNER") {
                navigate("/subscription-manage");
              } else {
                navigate("/page-not-found");
              }
            }}
            title={getPopupTitle(subscriptionDetails, t)}
            content={getPopupContent(subscriptionDetails, t)}
            cancelText={t("RAL0177")}
            confirmText={t("RAL0848")}
          />
        )} 

     {!["OWNER"].includes(CommonUtil.getRoleCode()) && ( 
          <RestrictPermissionPopup
            open={restrictPopupOpen}
            onClose={() => setRestrictPopupOpen(false)}
            title={getPopupTitle(subscriptionDetails, t, true)}
            content={getPopupContent(subscriptionDetails, t, true)}
            cancelText={t("RAL0177")}
          />
        )}
    </div>
  );
}
